import { GetStaticProps } from 'next';
import { useMemo, useState } from 'react';

import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import { Waypoint } from 'react-waypoint';
import { useBoolean, useWindowSize } from 'codekit';

import * as Icons from 'icons';
import Button from 'components/Button';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Project from 'components/Project';
import FaqQuestion from 'components/FaqQuestion';
import FaqSwitcher from 'components/FaqSwitcher';
import InfoGraphicHowItWorks from 'components/InfoGraphicHowItWorks';
import ScrambleText from 'components/ScrambleText';
import { useData } from 'contexts/DataContext';
import { faq, faqVariants, howItWorksSteps, ourNumbers } from 'constants/home';
import { FaqCategory } from 'models/Faq';

import { HomeContainer, Section } from './styles';

function Home(): JSX.Element {
	// Hooks
	const data = useData();
	const { isDesktop } = useWindowSize();

	// States
	const [faqCategory, setFaqCategory] = useState<FaqCategory>('process');

	// Boolean hooks
	const animateCountUp = useBoolean();

	// Memo vars
	const scramblePhases = useMemo(() => {
		const phases = [
			'destacar a sua empresa',
			'encantar seus clientes',
			'fidelizar audiências',
			'otimizar seus processos',
			isDesktop
				? 'impulsionar o seu crescimento'
				: 'impulsionar crescimento',
		];

		return phases;
	}, [isDesktop]);

	const projects = useMemo(() => {
		return data.projects.data.filter((project) => project.highlight);
	}, [data.projects.data]);

	// Functions
	function handleCountUp(start: () => void) {
		return () => {
			if (!animateCountUp.value) {
				animateCountUp.setTrue();
				start();
			}
		};
	}

	return (
		<HomeContainer>
			<Header />

			<section id="banner">
				<div className="content">
					<div className="text">
						<small>
							Soluções Personalizadas <Icons.FiZap />
						</small>

						<h1>
							Transformando ideias em <br />
							projetos excepcionais: {''}
							<span>
								<span>
									Soluções <br /> para{' '}
									<br className="tablet" />
								</span>{' '}
								<ScrambleText phrases={scramblePhases} />
							</span>
						</h1>

						<p>
							Desenvolvimento elegante e funcional para destacar a
							sua empresa online. Vamos criar uma presença digital
							de sucesso juntos.
						</p>

						<div className="buttons">
							<Button linkTo="/contact">
								Iniciar projeto <Icons.FiArrowRight />
							</Button>

							<Button variant="text" linkTo="#projects">
								Conhecer portfólio <Icons.FiCornerRightDown />
							</Button>
						</div>
					</div>

					<div className="gradient" />
				</div>
			</section>

			<section id="our-numbers">
				<div className="content">
					<div className="our-numbers">
						{ourNumbers.map((numbers) => (
							<div key={numbers.label} className="numbers-item">
								<strong>
									<CountUp
										start={0}
										end={numbers.value}
										duration={1.5}
										separator={'.'}
										prefix="+"
										suffix={numbers.suffix}
										useEasing
										redraw
									>
										{({ countUpRef, start }) => (
											<Waypoint
												onEnter={handleCountUp(start)}
											>
												<span ref={countUpRef} />
											</Waypoint>
										)}
									</CountUp>{' '}
								</strong>
								<p>{numbers.label}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			<Section id="projects">
				<div className="content">
					<div className="heading">
						<h1>
							Conheça nosso trabalho e os projetos que <br />
							criamos para nossos parceiros
						</h1>
					</div>

					<div className="projects">
						{projects.map((project) => (
							<Project key={project.id} project={project} />
						))}
					</div>

					<div className="footering">
						<div className="text">
							<p>
								Explore toda a nossa diversidade de soluções e
								projetos já desenvolvidos.
							</p>
						</div>

						<Button>
							Ver mais trabalhos <Icons.FiArrowRight />
						</Button>
					</div>
				</div>
			</Section>

			<Section id="how-it-works">
				<div className="content">
					<div className="heading">
						<h1>Conheça as etapas do nosso processo de criação</h1>

						<p></p>
					</div>

					<div className="how-it-works">
						<div className="steps">
							{howItWorksSteps.map((step) => (
								<div key={step.ref} className="step">
									<div className="number">{step.id}</div>

									<strong>{step.name}</strong>

									<p>{step.description}</p>
								</div>
							))}
						</div>

						<div className="infographic">
							<InfoGraphicHowItWorks />
						</div>
					</div>
				</div>
			</Section>

			<Section id="faq">
				<div className="content">
					<div className="heading">
						<h1>Perguntas frequentes</h1>
					</div>

					<div className="faq">
						<FaqSwitcher
							faqCategory={faqCategory}
							onChange={(category) => setFaqCategory(category)}
						/>

						<motion.div
							className="questions"
							variants={faqVariants.container}
							initial="hidden"
							animate="visible"
						>
							{faq[faqCategory].questions.map((question) => (
								<FaqQuestion
									key={question.id}
									question={question}
								/>
							))}
						</motion.div>
					</div>
				</div>
			</Section>

			<Footer />
		</HomeContainer>
	);
}

export const getStaticProps: GetStaticProps = () => {
	return {
		props: {
			data: {},
		},
	};
};

export default Home;
